import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "../../shadcn/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Badge } from "../../shadcn/ui/badge";
import { Plus, Minus, Users, Bell, Gift, Share2 } from "lucide-react";
import toastUtils from "../../utils/toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../shadcn/ui/dialog";
import { Label } from "../../shadcn/ui/label";
import { Input } from "../../shadcn/ui/input";
import * as localService from "../../services/localService";
import billingService from "../../services/billingService";
import logger from "../../utils/logger";
import { useCurrencies } from "../../hooks/useCurrencies";
import {
  getSubscriptionQuantity,
  calculateSubscriptionCost,
  getMaxUsersFromUser,
  isQuantityEditable,
  getOfferLimitParts,
} from "../../utils/subscriptionUtils";
import { useServiceSubcategories } from "../../hooks/useServiceSubcategories";
import { useSubscription } from "../../hooks/useSubscription";
import { fetchSubscriptions } from "../../redux/actions/subscription";
import { showApiMessage } from "../../utils/messageHandler";

const renderOfferLimit = (offer, pkg, serviceSubcategories) => {
  const { subcategoryName, period, checkIns } = getOfferLimitParts(offer, pkg, serviceSubcategories);
  return (
    <span>
      You can book all <strong className="font-bold text-gray-900">{subcategoryName}</strong> for{" "}
      <strong className="font-bold text-gray-900">{period}</strong>{" "}
      <strong className="font-bold text-gray-900">{checkIns}</strong>.
    </span>
  );
};

const MembershipPlan = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [standardPackages, setStandardPackages] = useState([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [packageError, setPackageError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [purchasedAddOns, setPurchasedAddOns] = useState([]);
  const [showPurchasedAddOnsModal, setShowPurchasedAddOnsModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const { currencies } = useCurrencies();
  const { activeSubscription, loading: subscriptionLoading, error: subscriptionError } = useSubscription();
  const hasActiveSubscription = !!activeSubscription;
  const { serviceSubcategories } = useServiceSubcategories();

  const refreshSubscription = useCallback(async () => {
    const currentUser = localService.getUser();
    const organizationId = currentUser?.organizationId;

    if (!organizationId) {
      logger.warn("No organization ID found for current user");
      return;
    }

    try {
      await dispatch(fetchSubscriptions(organizationId));
    } catch (error) {
      logger.error("Error refreshing organization subscriptions:", error);
    }
  }, [dispatch]);

  const fetchStandardPackages = useCallback(async () => {
    try {
      setIsLoadingPackages(true);
      const response = await billingService.getApplicablePackages({ limit: 10 });
      if (response?.data?.data) {
        setStandardPackages(response.data.data);
      }
    } catch (error) {
      logger.error("Error fetching standard packages:", error);
      setPackageError("Failed to load packages. Please try again.");
    } finally {
      setIsLoadingPackages(false);
    }
  }, []);

  useEffect(() => {
    // Always refresh subscription data on component mount to ensure we get fresh data
    // This handles cases where Redux might have stale data from previous sessions
    const forceRefreshSubscription = async () => {
      const currentUser = localService.getUser();
      const organizationId = currentUser?.organizationId;

      if (organizationId) {
        try {
          await dispatch(fetchSubscriptions(organizationId));

          // Fetch purchased add-ons
          const addOnsResponse = await billingService.getOrganizationSubscriptions({
            organizationId,
            limit: 100,
            orderBy: "subscriptionDate:desc",
            status: "USABLE",
            recurring: false,
          });

          const addOnSubscriptions = addOnsResponse?.data?.data || [];
          if (addOnSubscriptions.length > 0) {
            const purchasedAddOnsList = addOnSubscriptions.map(sub => ({
              id: sub.id,
              packageId: sub.packageId || sub.package?.id,
              name: sub.package?.name || sub.servicePackage?.name || "Add-on",
              description: sub.package?.description || sub.servicePackage?.description || "",
              amount: sub.amount || sub.package?.amount || "0",
              currencyId: sub.currencyId || sub.currency || sub.package?.currencyId || 4,
              quantity: sub.quantity || 1,
              subscriptionDate: sub.subscriptionDate,
            }));
            setPurchasedAddOns(purchasedAddOnsList);
          } else {
            setPurchasedAddOns([]);
          }
        } catch (error) {
          logger.error("Error forcing refresh of organization subscriptions:", error);
          setPurchasedAddOns([]);
        }
      }
    };

    forceRefreshSubscription();
    fetchStandardPackages();
  }, [dispatch, fetchStandardPackages, location.pathname]);

  useEffect(() => {
    if (activeSubscription) {
      localService.saveActiveSubscription(activeSubscription);
    } else {
      localService.clearActiveSubscription();
    }
  }, [activeSubscription]);

  // Refresh subscription data when window regains focus
  useEffect(() => {
    const handleWindowFocus = () => {
      const currentUser = localService.getUser();
      const organizationId = currentUser?.organizationId;
      if (organizationId) {
        dispatch(fetchSubscriptions(organizationId)).catch(error => {
          logger.error("Error refreshing subscriptions on window focus:", error);
        });
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [dispatch]);

  const handlePackageSelect = pkg => {
    setSelectedPackage(pkg);
    const user = localService.getUser();
    setQuantity(getSubscriptionQuantity(user, pkg));
    setShowConfirmModal(true);
  };

  const handleSubscribe = async () => {
    if (!selectedPackage || isSubscribing) return;

    try {
      setIsSubscribing(true);

      const user = localService.getUser();
      if (!user?.organizationId) {
        showApiMessage({ response: { status: 400, data: { message: "Organization ID not found" } } }, "subscribe");
        setIsSubscribing(false);
        return;
      }

      logger.log("Creating subscription with:", {
        organizationId: user.organizationId,
        packageId: selectedPackage.id,
        quantity: quantity,
      });

      const response = await billingService.createOrganizationSubscription({
        organizationId: user.organizationId,
        packageId: selectedPackage.id,
        quantity: quantity,
      });

      showApiMessage(response, "create subscription");

      if (response.data?.data?.checkoutUrl) {
        const checkoutUrl = response.data.data.checkoutUrl;
        // Close modal before redirecting
        setShowConfirmModal(false);
        setIsSubscribing(false);
        // Open Paystack checkout in a new tab
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.location.href = checkoutUrl;
        } else {
          // Fallback if popup blocker prevents new window
          window.location.href = checkoutUrl;
        }
      } else {
        // If no checkout URL, close modal and redirect to dashboard
        setShowConfirmModal(false);
        setIsSubscribing(false);
        history.push("/dashboard");
      }
    } catch (error) {
      showApiMessage(error, "create subscription");
      setIsSubscribing(false);
      // Keep modal open on error so user can try again
    }
  };
  const formatCurrency = (amount, currencyId) => {
    const currency = currencies.find(c => c.id === currencyId);
    const code = currency?.code || "KES";
    return `${code} ${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12 relative">
          <div className="absolute top-0 right-0 flex items-center gap-2">
            {/* Purchased Add-ons Notification */}
            {purchasedAddOns.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowPurchasedAddOnsModal(true)}
                  className="relative flex items-center justify-center w-10 h-10 bg-white rounded-full border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Bell className="w-5 h-5 text-gray-700" />
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-500 rounded-full">
                    {purchasedAddOns.length}
                  </span>
                </button>
              </div>
            )}
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {subscriptionLoading ? "Loading..." : hasActiveSubscription ? "Your Membership" : "Choose Your Plan"}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {subscriptionLoading
              ? "Checking your current subscription..."
              : hasActiveSubscription
                ? "Manage your current subscription and explore upgrade options"
                : "Select the perfect plan for your workspace needs"}
          </p>
        </div>

        {/* Subscription Error */}
        {!subscriptionLoading && subscriptionError && (
          <Card className="mb-8 border-red-200">
            <CardContent className="py-4 text-center">
              <p className="text-red-600">{subscriptionError}</p>
              <Button onClick={refreshSubscription} variant="outline" className="mt-4">
                Retry Loading Subscription
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {subscriptionLoading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Current Subscription */}
        {!subscriptionLoading && hasActiveSubscription && activeSubscription && (
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl">{activeSubscription.servicePackage?.name}</CardTitle>
                  <CardDescription className="text-lg">Active Subscription</CardDescription>
                </div>
                <Badge variant="default" className="bg-green-500 text-white">
                  Active
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Price</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(activeSubscription.amount, activeSubscription.currencyId)}
                    {activeSubscription.servicePackage?.recurring ? "/month" : ""}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Next Renewal</p>
                  <p className="text-lg font-semibold">
                    {activeSubscription.nextRenewalDate
                      ? new Date(activeSubscription.nextRenewalDate).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Current Team Capacity</p>
                  <p className="text-lg font-semibold">
                    {(() => {
                      const currentUser = localService.getUser();
                      return currentUser?.organization?.config?.maxUsers || 0;
                    })()}
                  </p>
                </div>
              </div>

              {activeSubscription.servicePackage?.offers && activeSubscription.servicePackage.offers.length > 0 && (
                <div className="mt-6 border-t pt-4 mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Package Features & Booking Limits:</h4>
                  <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700">
                    {activeSubscription.servicePackage.offers.map((offer, idx) => (
                      <li key={offer.id || idx}>
                        {renderOfferLimit(offer, activeSubscription.servicePackage, serviceSubcategories)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-4">
                <Button onClick={() => history.push("/billing-management")} variant="outline">
                  Manage Subscription
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Packages */}
        {!subscriptionLoading && !hasActiveSubscription && !subscriptionError && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Available Plans</h2>
              <p className="text-gray-600">Choose the plan that best fits your needs</p>
            </div>

            {isLoadingPackages ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : packageError ? (
              <Card>
                <CardContent className="text-center py-12">
                  <p className="text-red-600 mb-4">{packageError}</p>
                  <Button onClick={fetchStandardPackages} variant="outline">
                    Try Again
                  </Button>
                </CardContent>
              </Card>
            ) : standardPackages.length > 0 ? (
              <div
                className={`grid grid-cols-1 gap-6 justify-center ${
                  standardPackages.length === 1
                    ? "max-w-md mx-auto"
                    : standardPackages.length === 2
                      ? "md:grid-cols-2 max-w-3xl mx-auto"
                      : "md:grid-cols-2 lg:grid-cols-3"
                }`}
              >
                {standardPackages.map((pkg, index) => (
                  <Card key={pkg.id || index} className="cursor-pointer transition-all hover:shadow-md">
                    {index === 0 && (
                      <div className="text-center pt-4">
                        <Badge variant="default" className="bg-blue-500 text-white">
                          Most Popular
                        </Badge>
                      </div>
                    )}

                    <CardHeader className="text-center">
                      <CardTitle className="text-xl">{pkg.name}</CardTitle>
                      <CardDescription>{pkg.description}</CardDescription>
                    </CardHeader>

                    <CardContent>
                      <div className="text-center mb-6">
                        <div className="text-3xl font-bold text-gray-900">
                          {formatCurrency(pkg.amount, pkg.currencyId)}
                        </div>
                        <div className="text-gray-500">{pkg.recurring ? "per month" : "one-time"}</div>
                      </div>

                      <div className="space-y-2 mb-6">
                        <div className="flex items-center text-sm">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                          Max {pkg.maximumTeamSize || 0} team members
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                          Up to {pkg.maximumServiceProvidersAllowed || 0} spaces
                        </div>
                        <div className="flex items-center text-sm">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                          {pkg.validity || 0} {pkg.validityTimeUnit?.toLowerCase() || "days"} validity
                        </div>
                        {pkg.offers && pkg.offers.length > 0 && (
                          <div className="pt-3 border-t mt-3 space-y-1">
                            {pkg.offers.map((offer, idx) => (
                              <div key={offer.id || idx} className="flex items-start text-xs text-gray-600">
                                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                                <span>{renderOfferLimit(offer, pkg, serviceSubcategories)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Button className="w-full" variant="default" onClick={() => handlePackageSelect(pkg)}>
                        Subscribe
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <p className="text-gray-600">No packages available at the moment.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {selectedPackage && (
        <Dialog
          open={showConfirmModal}
          onOpenChange={open => {
            if (!open && !isSubscribing) {
              setShowConfirmModal(false);
              setSelectedPackage(null);
              setIsSubscribing(false);
            }
          }}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-gray-900">{selectedPackage.name}</DialogTitle>
              <DialogDescription className="text-lg text-gray-600">
                {selectedPackage.description || "Review your subscription details before confirming"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Price Display */}
              <div className="text-center border-b pb-4">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {formatCurrency(selectedPackage.amount, selectedPackage.currencyId)}
                </div>
                <div className="text-gray-600">{selectedPackage.recurring ? "per month" : "one-time"}</div>
              </div>

              {/* Pricing Breakdown */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Pricing</h4>
                {(() => {
                  const currentUser = localService.getUser();
                  const canEditQuantity = isQuantityEditable(currentUser, selectedPackage);
                  const currentQuantity = canEditQuantity
                    ? quantity
                    : getSubscriptionQuantity(currentUser, selectedPackage);
                  const { perUserCost, totalCost } = calculateSubscriptionCost(selectedPackage, currentQuantity);
                  const today = new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });

                  return (
                    <>
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 leading-tight mb-1">{selectedPackage.name}</h4>
                        {selectedPackage.description && (
                          <p className="text-sm text-gray-600 mb-4">{selectedPackage.description}</p>
                        )}
                      </div>

                      {/* Quantity Selector */}
                      {canEditQuantity && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <Label htmlFor="quantity" className="text-sm font-medium text-gray-700 mb-3 block">
                            Number of Users
                          </Label>
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 flex-shrink-0 bg-white"
                              onClick={() => setQuantity(Math.max(1, quantity - 1))}
                              disabled={quantity <= 1 || isSubscribing}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Input
                              id="quantity"
                              type="number"
                              min="1"
                              value={quantity}
                              onChange={e => {
                                const newQuantity = parseInt(e.target.value, 10) || 1;
                                setQuantity(Math.max(1, newQuantity));
                              }}
                              className="w-full text-center h-10 border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                              disabled={isSubscribing}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 flex-shrink-0 bg-white"
                              onClick={() => {
                                setQuantity(quantity + 1);
                              }}
                              disabled={isSubscribing}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          {selectedPackage.maximumTeamSize && (
                            <p className="text-xs text-gray-500 mt-2">
                              Maximum: {selectedPackage.maximumTeamSize} users
                            </p>
                          )}
                        </div>
                      )}

                      {!canEditQuantity && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-blue-800">
                            {selectedPackage.maximumTeamSize > 1
                              ? "Fixed team size"
                              : `Matches your team size (${currentQuantity} users)`}
                          </span>
                        </div>
                      )}

                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 shadow-sm overflow-hidden">
                        <div className="flex justify-between items-center p-3.5 bg-white">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Per User</div>
                            <div className="text-xs text-gray-500">Base price</div>
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {formatCurrency(perUserCost, selectedPackage.currencyId)}
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-3.5 bg-white">
                          <div>
                            <div className="text-sm font-medium text-gray-900">Users</div>
                            <div className="text-xs text-gray-500">Team size</div>
                          </div>
                          <div className="text-base font-semibold text-gray-900">× {currentQuantity}</div>
                        </div>
                        <div className="flex justify-between items-center p-3.5 bg-gray-50">
                          <div>
                            <div className="text-sm font-bold text-gray-900 text-blue-900">Total</div>
                            <div className="text-xs text-gray-500">
                              {selectedPackage.recurring ? "Monthly" : "One-time"}
                            </div>
                          </div>
                          <div className="text-xl font-bold text-blue-900">
                            {formatCurrency(totalCost, selectedPackage.currencyId)}
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Details</h4>
                        <div className="space-y-2.5">
                          <DetailRow icon="calendar" label="Starts" value="Today" helper={today} />
                          <DetailRow
                            icon="cycle"
                            label="Billing"
                            value={selectedPackage.recurring ? "Monthly" : "One-time"}
                            helper={selectedPackage.recurring ? "Auto-renews" : "Single payment"}
                          />
                          <DetailRow icon="shield" label="Cancellation" value="Anytime" helper="No penalties" />
                        </div>
                      </div>

                      {selectedPackage?.offers && selectedPackage.offers.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-100">
                          <h4 className="text-sm font-medium text-gray-700 mb-2.5">Booking Limits</h4>
                          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                            {selectedPackage.offers.map((offer, idx) => (
                              <li key={offer.id || idx}>
                                {renderOfferLimit(offer, selectedPackage, serviceSubcategories)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {isSubscribing && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-blue-800">Processing your subscription...</p>
                </div>
              </div>
            )}
            <DialogFooter className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  if (!isSubscribing) {
                    setShowConfirmModal(false);
                    setSelectedPackage(null);
                    setIsSubscribing(false);
                  }
                }}
                disabled={isSubscribing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubscribe}
                disabled={isSubscribing}
                className="bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubscribing ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </div>
                ) : (
                  "Confirm Subscription"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Purchased Add-ons Modal */}
      <Dialog open={showPurchasedAddOnsModal} onOpenChange={setShowPurchasedAddOnsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Your Purchased Add-ons</DialogTitle>
            <DialogDescription className="text-gray-600">View all your active add-on purchases</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            {purchasedAddOns.length > 0 ? (
              purchasedAddOns.map((addOn, index) => (
                <Card
                  key={addOn.id || index}
                  className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white"
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-semibold text-gray-900">{addOn.name}</CardTitle>
                        <CardDescription className="mt-1 text-gray-600">
                          {addOn.description || "Add-on feature"}
                        </CardDescription>
                      </div>
                      <Badge className="bg-green-600 text-white flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        Active
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Price</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatCurrency(addOn.amount, addOn.currencyId)}
                        </p>
                        <p className="text-xs text-gray-500">One-time payment</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Quantity</p>
                        <p className="text-lg font-semibold text-gray-900">{addOn.quantity}</p>
                        {addOn.subscriptionDate && (
                          <p className="text-xs text-gray-500 mt-1">
                            Purchased: {new Date(addOn.subscriptionDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8">
                <Gift className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">No purchased add-ons found</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailRow = ({ icon, label, value, helper }) => {
  const iconPaths = {
    calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    cycle:
      "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
    shield:
      "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  };

  return (
    <div className="flex items-start justify-between py-2">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPaths[icon]} />
        </svg>
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="text-right">
        <div className="text-sm font-medium text-gray-900">{value}</div>
        {helper && <div className="text-xs text-gray-500">{helper}</div>}
      </div>
    </div>
  );
};

export default MembershipPlan;
